<template>
  <b-container class="p-3" style="box-sizing: border-box; height: 100vh;">
    <b-row align-h="center" align-v="center" class="mb-3">
      <b-col md="7" lg="5">
        <form-header />
        <box class="p-5">
          <unusual-events
            v-if="isForm"
            :state="failedSubmitCount % 5 === 0 && failedSubmitCount !== 0"
          >
            <b-form @submit.prevent="submit()">
              <b-form-group>
                <b-form-label
                  slot="label"
                  :label="$t('email')"
                  for="email"
                  :errors="errors.email"
                />
                <b-form-input
                  v-model="user.email"
                  size="lg"
                  name="email"
                  id="email"
                  type="email"
                  tabindex="1"
                  required
                />
              </b-form-group>
              <b-form-group class="mb-0">
                <b-form-label
                  slot="label"
                  :label="$t('password')"
                  for="password"
                  :errors="errors.password"
                />
                <b-form-input
                  v-model="user.password"
                  size="lg"
                  name="password"
                  id="password"
                  tabindex="2"
                  required
                  type="password"
                  :state="errors.password ? false : null"
                />
                <div class="d-flex align-items-center">
                  <small class="text-light">
                    <router-link :to="{ name: 'forgot' }">
                      {{ $t('forgotPassword') }}?
                    </router-link>
                  </small>
                  <small class="text-light ml-auto">
                    <a href="#" @click.prevent="form = false">{{
                      $t('showAccounts')
                    }}</a>
                  </small>
                </div>
              </b-form-group>
              <b-button
                style="width: 100%;"
                variant="primary"
                class="mt-4 mb-1"
                id="sumbit-login"
                tabindex="3"
                type="submit"
              >
                {{ $t('signIn') }}
              </b-button>
              <small class="text-light">
                <span> {{ $t('needAccount') }}? </span>
                <router-link
                  :to="{ name: 'registration' }"
                  data-cy="register-link"
                >
                  {{ $t('register') }}
                </router-link>
              </small>
            </b-form>
          </unusual-events>
          <ul v-else class="profiles list-unstyled mb-0">
            <li class="text-center mb-3 font-weight-bold">
              {{ title }}
              <small
                class="d-block text-lowercase text-center"
                v-if="isRemovingAccounts"
              >
                {{ $t('fromThisBrowser') }}
              </small>
            </li>
            <li v-for="(value, key, index) in accounts" :key="index">
              <a
                :class="{
                  'd-block select-account': true,
                  'not-link': isRemovingAccounts
                }"
                href="#"
                @click.prevent="login(key)"
              >
                {{ key }}
              </a>
              <button
                v-if="isRemovingAccounts"
                class="remove p-0 border-0"
                @click="removeAccount(key)"
              >
                <svg-icon icon="minus-round" />
              </button>
            </li>
            <li class="divider mt-4 mb-4"></li>
            <li class="text-center">
              <a
                class="btn btn-link-alt switch-account"
                href="#"
                @click.prevent="form = true"
              >
                {{ $t('switchAccount') }}
              </a>
            </li>
            <li class="text-center" v-if="!isRemovingAccounts">
              <a
                class="btn btn-link-alt remove-account"
                href="#"
                @click.prevent="isRemovingAccounts = true"
              >
                {{ $t('removeAccount') }}
              </a>
            </li>
            <li v-if="isRemovingAccounts" class="done text-center">
              <button
                class="btn btn-sm btn-primary"
                @click.prevent="isRemovingAccounts = false"
              >
                {{ $t('done') }}
              </button>
            </li>
          </ul>
        </box>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import FormHeader from './components/FormHeader.vue';
import UnusualEvents from './components/UnusualEvents';
import * as auth from '@/utils/auth.js';

export default {
  head() {
    return {
      title() {
        return {
          inner: this.$t('metaTitle.login')
        };
      }
    };
  },

  components: {
    FormHeader,
    UnusualEvents
  },

  data() {
    return {
      user: {
        email: null,
        password: null
      },
      errors: {},
      failedSubmitCount: 0,
      form: false,
      isRemovingAccounts: false,
      accounts: {}
    };
  },

  computed: {
    isForm() {
      return Object.keys(this.accounts).length === 0 || this.form;
    },

    title() {
      return this.isRemovingAccounts
        ? this.$t('removeAccount')
        : this.$t('loginAs');
    }
  },

  methods: {
    setupUser() {
      if (!auth.readActiveRole() && auth.readToken()) {
        const adminIndex = this.$store.state.app.user.roles.indexOf(
          this.$config.rolesIds['admin']
        );

        this.$store.dispatch(
          'switchRole',
          this.$store.state.app.user.roles[adminIndex] ||
            this.$store.state.app.user.roles[0]
        );
      } else {
        this.$store.dispatch('switchRole', auth.readActiveRole());
      }

      if (auth.readActiveRole() === 'admin') {
        this.$router.replace({
          name: 'users'
        });
      } else if (auth.readActiveRole() === 'bounty-admin') {
        this.$router.replace({
          name: 'campaigns'
        });
      } else {
        this.$router.replace({
          name: 'dashboard'
        });
      }
    },

    removeAccount(email) {
      let tokens = JSON.parse(localStorage.getItem('jwt-refresh-tokens'));
      delete this.accounts[email];
      delete tokens[email];
      localStorage.setItem('jwt-tokens', JSON.stringify(this.accounts));
      localStorage.setItem('jwt-refresh-tokens', JSON.stringify(tokens));
      this.accounts = JSON.parse(localStorage.getItem('jwt-tokens'));
    },

    async login(email) {
      if (this.isRemovingAccounts) return;

      let tokens = JSON.parse(localStorage.getItem('jwt-refresh-tokens'));

      this.$api.updateCredentials({
        email: email,
        token: this.accounts[email],
        refreshToken: tokens[email]
      });

      await this.$store.dispatch('fetchUser');

      this.setupUser();
    },

    async submit() {
      this.errors = {};

      try {
        const res = await this.$api.auth.post('/auth/login', this.user);

        this.$api.updateCredentials({
          email: this.user.email,
          token: res.accessToken,
          refreshToken: res.refreshToken
        });

        await this.$store.dispatch('fetchUser');

        this.setupUser();
      } catch (e) {
        this.errors = {};
        this.failedSubmitCount++;

        if (e.response.data.error.errors) {
          this.errors = e.response.data.error
            ? this.$parseAuthErrors(e.response.data.error.errors)
            : {};
        } else {
          e.response.data.error.message &&
            this.$notify('alert', {
              text: e.response.data.error.message,
              variant: 'danger',
              time: 5000
            });
        }
      }
    }
  },

  created() {
    this.accounts = localStorage.getItem('jwt-tokens')
      ? JSON.parse(localStorage.getItem('jwt-tokens'))
      : {};
    this.form = !!localStorage.getItem('login-form');
  }
};
</script>

<style lang="scss" scoped>
.row {
  min-height: 100%;
}
.logo {
  border-bottom: none;
}
.empty-background {
  position: absolute;
  top: 0;
  max-width: 100%;
  width: 700px;
  left: 0;
}
.profiles {
  margin: 0 -1.5rem;

  li {
    position: relative;

    &:first-child {
      font-size: 1.2rem;
    }
  }

  a {
    padding: 0.5rem 1.5rem;
    text-decoration: none;
    color: $dark;
    transition: background-color 0.3s;

    &:hover {
      color: $dark;
      background-color: $border-color;
    }

    &.btn {
      display: inline-flex;
      background-color: transparent;
      transition: color 0.3s;

      &:hover {
        color: $primary;
      }
    }

    &.not-link {
      cursor: auto;

      &:hover {
        background-color: transparent;
      }
    }
  }

  .divider {
    margin: 1.25rem 1.5rem;
    border-top: 1px solid $border-color;
  }

  .done {
    margin: 0.2rem 1.5rem 0;
  }

  .remove {
    position: absolute;
    right: 1.5rem;
    top: 50%;
    transform: translateY(-50%);
    transition: color 0.3s;

    &:hover {
      color: $primary;
    }

    i {
      display: block;
    }
  }
}
</style>
